import {
  AssessmentSubject,
  gruposDeColeta as gruposDoProtocolo,
  specV1,
  traitsV1,
  validateAssessment,
  type Trait,
  type ValidationResult,
} from '@equiview/protocol';
import type { FichaDeColeta, Medida, SessaoDeCampo } from './fonte.js';

const sessao = (s: SessaoDeCampo | null, replica: number, medidas: readonly Medida[]) => ({
  evaluatorId: s?.evaluator_id ?? '',
  recorderId: s?.recorder_id ?? null,
  replicateNo: replica,
  method: 'campo' as const,
  observations: medidas.map((o) => ({
    trait: o.trait_code,
    side: o.side,
    value: o.value_num,
    notApplicable: o.not_applicable,
  })),
});

/**
 * Roda na ficha em coleta o MESMO engine que o servidor roda de noite
 * (`validateAssessment`, da spec). A tela nao tem regra propria: o que bloqueia
 * ou avisa aqui e exatamente o que o protocolo diz.
 */
export function validarFicha(f: FichaDeColeta): ValidationResult {
  const m = f.manejo;
  const subject = AssessmentSubject.parse({
    level: f.avaliacao.protocol_level,
    sex: f.animal.sex,
    birthDate: f.animal.birth_date,
    // Hora fixa: so a data importa para as regras (ferrageamento futuro, idade).
    assessedAt: `${f.visita.visit_date}T12:00:00.000Z`,
    sampledForDoubleMeasure: f.avaliacao.sampled_for_double_measure,
    // Sem sessao ainda, uma vazia: e ela que faz o engine cobrar as medidas. A
    // segunda (dupla medicao) entra quando existe; sem ela, a PRD-006 cobra.
    sessions: f.sessoes.length
      ? f.sessoes.map((s) => sessao(s, s.replicate_no, s.medidas))
      : [sessao(null, 1, [])],
    management: {
      weightKg: m.weight_kg,
      weightMethod: m.weight_method,
      bcsHenneke: m.bcs_henneke,
      lastFeedTime: m.last_feed_time,
      waterAccess: m.water_access,
      arrivedFarmGt30d: m.arrived_farm_gt_30d,
      femaleStatus: m.female_status,
      gestationMonths: m.gestation_months,
      daysPostpartum: m.days_postpartum,
      maleStatus: m.male_status,
      coversLastWeek: m.covers_last_week,
      trainingStatus: m.training_status,
      trainingMonths: m.training_months,
      lastShoeingDate: m.last_shoeing_date,
      shodFl: m.shod_fl,
      shodFr: m.shod_fr,
      shodHl: m.shod_hl,
      shodHr: m.shod_hr,
      barefoot: m.barefoot,
      hoofCondition: m.hoof_condition,
      fitness: m.fitness,
      lamenessLimb: m.lameness_limb,
      lamenessAaep: m.lameness_aaep,
      dnaSampleCollected: m.dna_sample_collected,
      dnaTubeId: m.dna_tube_id,
    },
  });
  return validateAssessment(specV1, subject);
}

/** A Estacao 3 na ordem em que se mede: um instrumento por vez (ver protocol/coleta.ts). */
export const gruposDeColeta = (nivel: number) => gruposDoProtocolo(traitsV1, nivel);

/** Lados esperados de um trait: E e D quando lateralizado, NA quando nao. */
export const ladosDe = (t: Trait) => (t.sided ? (['E', 'D'] as const) : (['NA'] as const));
